import { Check, ChevronDown } from "lucide-react";
import { useEffect, useId, useRef, useState } from "react";

import { controlClass, controlHeight } from "@/components/ui/field";
import { popoverStyle, usePopoverPosition } from "@/lib/use-popover-position";
import { cn } from "@/lib/utils";

export interface SelectChoice {
  value: string;
  label: string;
}

/**
 * A picker over a fixed list of choices, drawn by us.
 *
 * A native `<select>` opens the operating system's own menu — dark grey on
 * macOS, its own type, its own blue selection — so a filter that matched the
 * app everywhere else fell out of it the moment it was opened. The list here
 * wears the app's surfaces and its accent.
 *
 * Positioned like every other popover in the app: fixed, from the trigger's
 * rect, so the filters modal's scrolling body cannot clip it.
 *
 * For a list that has to be searched or paged, use `FkSelect` — this one is
 * for a handful of known values, so it has neither.
 *
 * Pass an array as `value` and it picks several: the menu stays open, each
 * choice toggles, and the button reads them as one line. A filter the endpoint
 * answers with a list is one control either way, so this is a mode rather than
 * a second component.
 */
export function OptionSelect<V extends string | string[]>({
  id,
  value,
  onChange,
  choices,
  placeholder = "Select…",
  disabled = false,
  className,
}: {
  id?: string | undefined;
  /** A string picks one; an array picks several. The generic carries which
   *  back to the caller, so `onChange` is handed the same shape it was given. */
  value: V;
  onChange: (value: V) => void;
  choices: SelectChoice[];
  placeholder?: string;
  disabled?: boolean;
  className?: string | undefined;
}) {
  const root = useRef<HTMLDivElement>(null);
  const menu = useRef<HTMLDivElement>(null);
  const listId = useId();
  const [open, setOpen] = useState(false);
  const box = usePopoverPosition(root, open);

  useEffect(() => {
    if (!open) return;
    const away = (event: PointerEvent) => {
      const target = event.target as Node;
      // The list hangs outside the root, so both count as "inside".
      if (!root.current?.contains(target) && !menu.current?.contains(target)) setOpen(false);
    };
    const key = (event: KeyboardEvent) => event.key === "Escape" && setOpen(false);
    document.addEventListener("pointerdown", away);
    document.addEventListener("keydown", key);
    return () => {
      document.removeEventListener("pointerdown", away);
      document.removeEventListener("keydown", key);
    };
  }, [open]);

  const many = Array.isArray(value);
  // Always a list of the chosen values, whichever shape came in, so the rest
  // of this component is written once.
  const picked: string[] = many ? (value as string[]) : value === "" ? [] : [value as string];
  const chosen = picked
    .map((one) => choices.find((choice) => choice.value === one)?.label ?? one)
    .join(", ");
  /**
   * Toggling in multi mode, replacing in single. Sorted, so the same set of
   * choices is the same value however it was clicked — which is what makes a
   * saved set match and a query key stable.
   *
   * The casts are the price of one component serving both: `many` is what
   * decides the shape, and the checker cannot see that it agrees with `V`.
   */
  const pick = (next: string) => {
    if (!many) return onChange(next as unknown as V);
    const kept = picked.includes(next)
      ? picked.filter((one) => one !== next)
      : [...picked, next];
    onChange([...kept].sort() as unknown as V);
  };

  return (
    <div ref={root} className={cn("relative", className)}>
      <button
        type="button"
        id={id}
        role="combobox"
        aria-expanded={open}
        aria-controls={listId}
        aria-haspopup="listbox"
        disabled={disabled}
        onClick={() => setOpen((was) => !was)}
        className={cn(
          controlClass,
          controlHeight.md,
          "flex items-center gap-2 text-left",
          open && "border-accent ring-[3px] ring-[var(--accent-ring)]",
        )}
      >
        <span className={cn("min-w-0 flex-1 truncate", !chosen && "text-fg-subtle")}>
          {chosen || placeholder}
        </span>
        <ChevronDown className="text-fg-subtle size-4 shrink-0" aria-hidden />
      </button>

      {open ? (
        <div
          ref={menu}
          style={popoverStyle(box)}
          className="border-line bg-surface-raised animate-sheet fixed z-50 origin-top overflow-hidden rounded-xl border shadow-[var(--shadow-float)]"
        >
          <ul id={listId} role="listbox" className="max-h-72 overflow-y-auto p-1">
            {/* The blank row clears; in multi mode it clears the lot. */}
            {[{ value: "", label: placeholder }, ...choices].map((choice) => {
              const selected =
                choice.value === "" ? picked.length === 0 : picked.includes(choice.value);
              return (
                <li key={choice.value} role="option" aria-selected={selected}>
                  <button
                    type="button"
                    onClick={() => {
                      if (choice.value === "") {
                        onChange((many ? [] : "") as unknown as V);
                        setOpen(false);
                        return;
                      }
                      pick(choice.value);
                      // Several values are picked in one visit; one is not.
                      if (!many) setOpen(false);
                    }}
                    className={cn(
                      "text-callout flex w-full items-center gap-2 rounded-lg px-2.5 py-2 text-left transition-colors",
                      selected
                        ? "bg-accent-soft text-accent font-medium"
                        : "text-fg hover:bg-surface-inset",
                    )}
                  >
                    <span className="min-w-0 flex-1 truncate">{choice.label}</span>
                    {selected ? <Check className="size-4 shrink-0" aria-hidden /> : null}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      ) : null}
    </div>
  );
}
